import { useState } from "react";
import { CalendarClock, Ban } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorText } from "@/lib/api";
import { fmtCompact as fmtM } from "@/lib/format";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const CapexMoveDialog = ({ projectId, budgetYear, item, mode, onClose, onDone }) => {
  const [year, setYear] = useState(budgetYear);
  const [month, setMonth] = useState(Math.min(12, (item?.month || 1) + 1));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const cancel = mode === "cancel";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = cancel
        ? await api.post(`/projects/${projectId}/capex/${item.id}/cancel`, { note })
        : await api.post(`/projects/${projectId}/capex/${item.id}/move`, { year: Number(year), month: Number(month), note });
      toast.success(cancel ? `'${item.title}' cancelled — ${fmtM(item.amount)} returns to the reserve` : Number(year) === budgetYear ? `'${item.title}' moved to ${MONTHS[month - 1]} ${year} (status unchanged)` : `'${item.title}' deferred to ${MONTHS[month - 1]} ${year}${data?.items ? "" : ""} — it will appear in that year's CAPEX list`);
      onDone(data);
      onClose();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="capex-move-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-base">{cancel ? <Ban size={16} className="text-red-600" /> : <CalendarClock size={16} className="text-emerald-700" />} {cancel ? "Cancel CAPEX item" : "Move CAPEX item"}</DialogTitle>
          <DialogDescription className="text-xs">
            <b>{item.title}</b> · {item.department}{item.vendor ? ` · ${item.vendor}` : ""} · <span className="font-mono">{fmtM(item.amount)}</span> · planned {MONTHS[(item.month || 1) - 1]} {budgetYear} · status <i>{item.status.replace("_", " ")}</i>
          </DialogDescription>
        </DialogHeader>
        {!cancel && (
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label className="space-y-1"><span className="text-slate-600">Move to year</span>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2" data-testid="capex-move-year">
                {[0, 1, 2, 3].map((k) => <option key={k} value={budgetYear + k}>{budgetYear + k}{k === 0 ? " (this budget)" : ""}</option>)}
              </select></label>
            <label className="space-y-1"><span className="text-slate-600">Month</span>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2" data-testid="capex-move-month">
                {MONTHS.map((m, k) => <option key={m} value={k + 1}>{m}</option>)}
              </select></label>
            <p className="col-span-2 text-[11px] text-slate-500">{Number(year) === budgetYear ? "Same year: only the planned month changes; the item stays approved and remains committed in the reserve schedule." : `Another year: the item is marked deferred here (released from the ${budgetYear} reserve) and added — same status, with vendor and amount — to the ${year} budget of this hotel, or parked until that budget is created.`}</p>
          </div>
        )}
        <label className="space-y-1 text-xs"><span className="text-slate-600">{cancel ? "Reason (required — shown to the department and GM)" : "Note (optional)"}</span>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={cancel ? "e.g. no longer required / replaced by …" : "e.g. vendor lead time"} className="h-9 text-xs" data-testid="capex-move-note" /></label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} data-testid="capex-move-cancel">Back</Button>
          <Button type="button" size="sm" onClick={submit} disabled={busy || (cancel && !note.trim())} className={cancel ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-900 hover:bg-emerald-700 text-white"} data-testid="capex-move-confirm">{busy ? "Saving…" : cancel ? "Cancel item & release budget" : Number(year) === budgetYear ? "Move month" : `Defer to ${year}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
